import type { BlockValues } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import type { BaseBlock } from "@circle-react-shared/BuilderUI/DataModel.types";
import { isNonNullableUnknown } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import { isResponsiveValue } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { flattenCssVariables } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/flattenCssVariables";
import { resolveCssVariable } from "@circle-react-shared/BuilderUI/ThemeConfiguration/SiteStyles/Tokens/utils/resolveCssVariable";
import type { ScreenSize } from "@circle-react-shared/ScreenSizeSelect";
import {
  isChildrenProp,
  isRecord,
  isStyleValue,
  isStylesProp,
} from "./typePredicates";

const processStyleProps = (
  styleValue: Record<string, unknown>,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
  canvasSize: ScreenSize,
): Record<string, unknown> =>
  Object.entries(styleValue).reduce<Record<string, unknown>>(
    (acc, [key, value]) => {
      if (isRecord(value) && !isStyleValue(value)) {
        acc[key] = processStyleProps(
          value,
          currentCanvasTheme,
          appearance,
          canvasSize,
        );
      } else {
        acc[key] = extractStyleValue(
          value,
          currentCanvasTheme,
          appearance,
          canvasSize,
        );
      }
      return acc;
    },
    {},
  );

const resolveIfCssVariable = (
  value: unknown,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
): unknown => {
  if (typeof value === "string" && value.startsWith("var(--")) {
    return resolveCssVariable({
      value,
      cssVariables: flattenCssVariables(
        currentCanvasTheme.cssVariables,
        appearance,
      ),
    });
  }
  return value;
};

const resolveRecordValues = (
  record: Record<string, unknown>,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
): Record<string, unknown> =>
  Object.entries(record).reduce<Record<string, unknown>>((acc, [key, val]) => {
    acc[key] = resolveIfCssVariable(val, currentCanvasTheme, appearance);
    return acc;
  }, {});

const extractCanvasSizeValue = (
  value: Record<string, unknown>,
  canvasSize: ScreenSize,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
): unknown => {
  const canvasSizeValue = value[canvasSize];
  if (typeof canvasSizeValue === "string") {
    return resolveIfCssVariable(
      canvasSizeValue,
      currentCanvasTheme,
      appearance,
    );
  }
  if (isRecord(canvasSizeValue)) {
    return resolveRecordValues(canvasSizeValue, currentCanvasTheme, appearance);
  }
  return canvasSizeValue;
};

const extractStyleValue = (
  prop: unknown,
  currentCanvasTheme: SiteTheme,
  appearance: Appearance,
  canvasSize: ScreenSize,
): unknown => {
  if (isStyleValue(prop)) {
    const value = prop.value;
    if (typeof value === "string") {
      return resolveIfCssVariable(value, currentCanvasTheme, appearance);
    }
    if (isRecord(value)) {
      const hasCanvasSizeStructure = isResponsiveValue(value);
      if (hasCanvasSizeStructure) {
        return extractCanvasSizeValue(
          value,
          canvasSize,
          currentCanvasTheme,
          appearance,
        );
      }
      return resolveRecordValues(value, currentCanvasTheme, appearance);
    }
    return value;
  }
  if (isRecord(prop)) {
    return prop.value;
  }
  return prop;
};

const blockToValues = ({
  block,
  currentCanvasTheme,
  appearance,
  canvasSize,
}: {
  block: BaseBlock;
  currentCanvasTheme: SiteTheme;
  appearance: Appearance;
  canvasSize: ScreenSize;
}): BlockValues => {
  const values: BlockValues = {};

  const extractValues = (props: BaseBlock["props"]): void => {
    Object.entries(props).forEach(([key, prop]) => {
      if (isChildrenProp(prop)) {
        return;
      }

      if (isStylesProp(prop)) {
        values[key] = processStyleProps(
          prop.value,
          currentCanvasTheme,
          appearance,
          canvasSize,
        );

        return;
      }

      if (isNonNullableUnknown(prop.value)) {
        values[key] = prop.value;
      }
    });
  };

  extractValues(block.props);

  return values;
};

export { blockToValues };
